import { useState } from 'react';
import ReactCrop, { type Crop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface ImageCropperProps {
  imageFile: File;
  onCropComplete: (croppedImage: File) => void;
  onCancel: () => void;
  aspectRatio?: number;
}

function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number,
) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  );
}

export const ImageCropper = ({ 
  imageFile, 
  onCropComplete, 
  onCancel,
  aspectRatio = 1 
}: ImageCropperProps) => {
  const [imgSrc, setImgSrc] = useState<string>('');
  const [crop, setCrop] = useState<Crop>();
  const [imgRef, setImgRef] = useState<HTMLImageElement>();

  // Load image immediately when component mounts
  useState(() => {
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      setImgSrc(reader.result as string);
    });
    reader.readAsDataURL(imageFile);
  });

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    const { width, height } = e.currentTarget;
    setImgRef(e.currentTarget);
    setCrop(centerAspectCrop(width, height, aspectRatio));
  }

  const getCroppedImg = async (image: HTMLImageElement, crop: Crop): Promise<File> => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('No 2d context');
    }

    // Set canvas size to desired output size
    const outputSize = 400;
    canvas.width = outputSize;
    canvas.height = outputSize;

    // Create circular clipping path
    ctx.beginPath();
    ctx.arc(outputSize / 2, outputSize / 2, outputSize / 2, 0, Math.PI * 2);
    ctx.clip();

    // Calculate source and destination coordinates
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const sourceX = (crop.x * scaleX);
    const sourceY = (crop.y * scaleY);
    const sourceWidth = (crop.width * scaleX);
    const sourceHeight = (crop.height * scaleY);

    // Draw the cropped image
    ctx.drawImage(
      image,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      outputSize,
      outputSize
    );

    // Convert to file
    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            throw new Error('Canvas is empty');
          }
          const croppedFile = new File([blob], imageFile.name, {
            type: 'image/png',
            lastModified: Date.now(),
          });
          resolve(croppedFile);
        },
        'image/png',
        1
      );
    });
  };

  const handleCropComplete = async () => {
    if (!imgRef || !crop) return;

    try {
      const croppedImage = await getCroppedImg(imgRef, crop);
      onCropComplete(croppedImage);
    } catch (error) {
      console.error('Error cropping image:', error);
    }
  };

  return (
    <Dialog open={true} onOpenChange={() => onCancel()}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Adjust Profile Picture</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          {imgSrc && (
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              aspect={aspectRatio}
              circularCrop
              className="max-h-[400px] overflow-auto"
            >
              <img 
                src={imgSrc} 
                alt="Crop preview" 
                className="max-w-full"
                onLoad={onImageLoad}
              />
            </ReactCrop>
          )}
        </div>
        <DialogFooter className="flex-col space-y-2 sm:space-y-0">
          <Button variant="outline" onClick={onCancel} className="w-full sm:w-auto">Cancel</Button>
          <Button onClick={handleCropComplete} className="w-full sm:w-auto">Apply</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};